/**
 * QChatInput Component (v3.0 - Bolt/Claude Inspired)
 * Clean, minimal chat input with professional styling
 *
 * Key Improvements v3.0:
 * - Bolt.new inspired design
 * - Pure dark background (#0A0A0F)
 * - Crisp placeholder text
 * - Minimal styling, professional look
 */

import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box, TextField, IconButton } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';

/**
 * QChatInput Component
 * @param {function} onSend - Callback when message is sent
 * @param {function} onCancel - Callback to cancel streaming
 * @param {boolean} isLoading - Whether AI is responding
 * @param {boolean} disabled - Whether input is disabled
 * @param {boolean} expanded - Whether input is in expanded mode
 * @param {string} placeholder - Input placeholder text
 */
const QChatInput = forwardRef(({
  onSend,
  onCancel,
  isLoading = false,
  disabled = false,
  expanded = false,
  placeholder = 'Message Q...'
}, ref) => {
  const [message, setMessage] = useState('');
  const inputRef = useRef(null);

  // Expose focus method to parent
  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus()
  }));

  // Focus input on mount and when not loading
  useEffect(() => {
    if (inputRef.current && !isLoading) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  const handleSend = () => {
    if (message.trim() && !isLoading && !disabled) {
      onSend(message.trim());
      setMessage('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const hasContent = message.trim().length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 0.75,
        transition: 'all 0.12s ease'
      }}
    >
      {/* Text Input - Compact Bolt style */}
      <TextField
        inputRef={inputRef}
        fullWidth
        multiline
        minRows={expanded ? 3 : 1}
        maxRows={expanded ? 6 : 3}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(24, 24, 27, 0.6)',
            borderRadius: '6px',
            color: '#F5F5F5',
            fontSize: '0.875rem',
            lineHeight: 1.5,
            minHeight: '36px',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.06)',
              borderWidth: '1px'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)'
            },
            '&.Mui-focused fieldset': {
              borderColor: 'rgba(39, 181, 109, 0.3)',
              borderWidth: '1px'
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(24, 24, 27, 0.3)',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.03)'
              }
            }
          },
          '& .MuiOutlinedInput-input': {
            padding: '8px 12px',
            '&::placeholder': {
              color: '#52525B',
              opacity: 1
            }
          }
        }}
      />

      {/* Send/Cancel Button - More Visible */}
      {isLoading ? (
        <IconButton
          onClick={handleCancel}
          sx={{
            backgroundColor: 'rgba(239, 68, 68, 0.15)',
            color: '#EF4444',
            width: 36,
            height: 36,
            borderRadius: '6px',
            flexShrink: 0,
            '&:hover': {
              backgroundColor: 'rgba(239, 68, 68, 0.2)'
            }
          }}
        >
          <StopIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      ) : (
        <IconButton
          onClick={handleSend}
          disabled={!hasContent || disabled}
          sx={{
            backgroundColor: hasContent && !disabled
              ? '#27B56D' // Solid green when active
              : 'rgba(39, 39, 42, 0.6)',
            color: hasContent && !disabled
              ? '#FFFFFF'
              : '#52525B',
            width: 36,
            height: 36,
            borderRadius: '6px',
            flexShrink: 0,
            border: 'none',
            '&:hover': {
              backgroundColor: hasContent && !disabled
                ? '#22A55E'
                : 'rgba(39, 39, 42, 0.8)'
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(39, 39, 42, 0.4)',
              color: '#3F3F46'
            }
          }}
        >
          <SendIcon sx={{ fontSize: '0.9375rem' }} />
        </IconButton>
      )}
    </Box>
  );
});

QChatInput.displayName = 'QChatInput';

export default QChatInput;
